import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useStore } from '@/store';

import { DeliveriesTab } from './components/DeliveriesTab';
import { useEndpointActions } from './components/EndpointActions';
import { EndpointsTab } from './components/EndpointsTab';
import { useWebhookEndpoints } from './hooks/useWebhookEndpoints';
import { useWebhooksOrg } from './hooks/useWebhooksOrg';
import { developerSettingsPath, parseWebhooksTab, type WebhooksTab } from './utils';

/**
 * Customer Webhooks section (org-scoped): Endpoints | Deliveries, with the tab
 * and the deep-link filters (`endpoint`, `delivery`) in the URL. One
 * organization only — there is no organization filter on the customer side.
 * Opened from Settings → Developer (it has no sidebar entry).
 */
export function WebhooksContainer() {
  const { organizationId, permissions } = useWebhooksOrg();
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: WebhooksTab = permissions.canListDeliveries
    ? parseWebhooksTab(searchParams.get('tab'))
    : 'endpoints';
  const endpointParam = searchParams.get('endpoint') ?? undefined;
  const deliveryParam = searchParams.get('delivery') ?? undefined;

  const endpointsQuery = useWebhookEndpoints(organizationId, permissions.canListEndpoints);
  const endpoints = endpointsQuery.data?.endpoints ?? [];
  const actions = useEndpointActions({ organizationId, permissions });

  const handleTabChange = useCallback(
    (value: string) => {
      const next = new URLSearchParams();
      if (value === 'deliveries') next.set('tab', 'deliveries');
      setSearchParams(next, { replace: true });
    },
    [setSearchParams],
  );

  const clearDeliveryParam = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('delivery');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  return (
    <>
      <BreadcrumbPortal>
        <Breadcrumb>
          <Breadcrumb.Item to={developerSettingsPath(organizationId, selectedProgramId)}>
            Settings
          </Breadcrumb.Item>
          <Breadcrumb.Item>Webhooks</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbPortal>

      <div className="h-full flex flex-col overflow-hidden">
        <div className="max-w-7xl mx-auto w-full px-6 pt-8 pb-2">
          <div className="mb-4">
            <h1 className="text-2xl font-bold">Webhooks</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Receive signed case events at your own endpoints, and watch and retry their
              deliveries.
            </p>
          </div>
          {permissions.canListDeliveries && (
            <Tabs value={tab} onValueChange={handleTabChange}>
              <TabsList>
                <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
                <TabsTrigger value="deliveries">Deliveries</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="max-w-7xl mx-auto w-full px-6 pb-6 pt-2 flex flex-col min-h-full">
            {tab === 'endpoints' ? (
              <EndpointsTab
                organizationId={organizationId}
                permissions={permissions}
                endpoints={endpoints}
                isLoading={endpointsQuery.isLoading}
                error={endpointsQuery.error}
                actions={actions}
              />
            ) : (
              <DeliveriesTab
                // Remount when a deep link changes the starting endpoint filter (and
                // per org). Not on `delivery`: closing the drawer drops that param,
                // and a remount would throw away the viewer's filters and page.
                key={`${organizationId}|${endpointParam ?? ''}`}
                organizationId={organizationId}
                permissions={permissions}
                endpoints={endpoints}
                initialEndpointId={endpointParam}
                initialDeliveryId={deliveryParam}
                onDeepLinkClosed={clearDeliveryParam}
              />
            )}
          </div>
        </div>
      </div>

      {actions.dialogs}
    </>
  );
}
